/* ----------------------------------------------------------------------------
 * social-mirror.ts — the Lane C WYSIWYG-mirror mapping (FE side of THE SHARED
 * SOCIAL POST MODEL). A PURE, network-free, secret-free, deterministic mapper
 * from a Studio draft's editable composition to the per-platform post that will
 * ACTUALLY be published — the FE twin of the backend
 * BkSesh\SalesApi\Modules\Social\Services\SocialPostModel::fromDraft().
 *
 * WHY IT MUST MATCH fromDraft() FIELD-FOR-FIELD: if the Studio preview mirror
 * derives from THIS mapping and the publisher publishes from fromDraft(), then
 * "what Studio shows == what posts" BY CONSTRUCTION, closing the historic
 * "baked-in graphic shown, raw media + caption posted" drift. Every branch here
 * is a 1:1 translation of the PHP; cite the PHP line when in doubt, do NOT
 * re-guess field selection.
 *
 * OWNER-APPROVED DECISIONS MIRRORED (planning gate 2026-08-07, Option A):
 *   - publish = RAW media (largeUrl ?? previewUrl) + caption (body_text verbatim).
 *     The Studio baked-in layout text is CSS PREVIEW ONLY — never rasterised, so
 *     it never ships. `relabelNeeded` flags exactly the surfaces that must carry
 *     the "Styling preview — posts as image + caption" label.
 *   - FB blog link => FIRST-COMMENT by default (never a caption URL); a per-post
 *     toggle may move it to caption-bottom instead (mutually exclusive).
 *   - IG = LINK-IN-BIO only: a link is NEVER placed in an IG caption or comment.
 *   - Media routing: photo -> FB/IG photo; video -> FB video + IG Reel;
 *     none -> FB text / IG blocked (ig_requires_media).
 *   - Only facebook/instagram are wired publish targets; every other channel
 *     (linkedin/x/generic/…) mirrors to a `blocked` post (unsupported_platform),
 *     which is the honest "this will NOT publish here".
 * -------------------------------------------------------------------------- */
import type { Channel } from '@/types/social';

/** The two wired publish targets (SocialPostModel::PLATFORM_*). */
export type MirrorPlatform = 'facebook' | 'instagram';
/** Post format (SocialPostModel::FORMAT_*). */
export type MirrorFormat = 'photo' | 'video' | 'reel' | 'text' | 'blocked';
/** Blog-link placement (SocialPostModel::LINK_*). */
export type MirrorLinkPlacement = 'first_comment' | 'caption_bottom' | 'none';
/** Honest blocked reasons (fromDraft blocked_reason). */
export type MirrorBlockedReason = 'ig_requires_media' | 'unsupported_platform' | null;

/** A page media slot as it arrives on the FE (SlotMedia + P0-A largeUrl). */
export interface MirrorMediaInput {
  kind?: string | null;
  largeUrl?: string | null;
  previewUrl?: string | null;
}

/**
 * The draft-shaped input. Fields are the FE analogues of the PHP draft keys read
 * by fromDraft():
 *   - channel      <- draft['channel']
 *   - bodyText     <- draft['body_text'] (already headline+body-joined upstream,
 *                     exactly as StudioEditorPage persists it; captionSource())
 *   - media        <- pages[0].slots.media (primaryMedia())
 *   - link / suggestedFirstComment / linkPlacement <- FB-only publish options
 *     (linkSource/firstCommentSource/$options['link_placement']). These are NOT
 *     surfaced by the current typed ContentItem, so they default to none/null —
 *     the first-comment indicator is correctly DORMANT until the draft carries
 *     them (documented; no BE field is invented here).
 */
export interface MirrorDraftInput {
  channel: Channel | string;
  bodyText: string;
  media?: MirrorMediaInput | null;
  link?: string | null;
  suggestedFirstComment?: string | null;
  linkPlacement?: MirrorLinkPlacement | null;
}

/** The per-platform "what will post" projection (FE twin of a PlatformPost). */
export interface MirrorPost {
  /** Resolved wired platform, OR the raw channel label when unsupported. */
  platform: MirrorPlatform | string;
  /** false => not a wired publish target (mirrors a blocked/unsupported post). */
  supported: boolean;
  format: MirrorFormat;
  /** The FINAL caption that posts (body_text verbatim; +link only on caption_bottom). */
  caption: string;
  /** The RAW media asset URL that ships (no baked overlay), or null. */
  mediaUrl: string | null;
  mediaKind: 'photo' | 'video' | null;
  /** The blog link ('' when none). */
  link: string;
  linkPlacement: MirrorLinkPlacement;
  /** Posted as the FB first comment when placement=first_comment; else null. Never for IG. */
  firstCommentNote: string | null;
  blockedReason: MirrorBlockedReason;
  /**
   * true when the Studio composition (baked/framed styling over a shipping raw
   * asset) diverges from the literal post — i.e. media ships raw. Drives the
   * non-dismissable "Styling preview — posts as image + caption" relabel.
   */
  relabelNeeded: boolean;
}

/** Normalised primary media, or null (mirrors primaryMedia()). */
export interface MirrorResolvedMedia {
  kind: 'photo' | 'video';
  url: string;
}

/**
 * url = largeUrl ?? previewUrl (mirrors primaryMedia()). A blob: URL is a local,
 * non-durable object that would NOT be what publishes, so it is dropped in
 * favour of any real source (mirrors pickMediaUrl()). Returns null when media is
 * absent OR has no resolvable URL — an honest "no media", never a fabricated URL.
 */
export function resolveMirrorMedia(m?: MirrorMediaInput | null): MirrorResolvedMedia | null {
  if (!m) return null;
  const large = typeof m.largeUrl === 'string' ? m.largeUrl.trim() : '';
  const prev = typeof m.previewUrl === 'string' ? m.previewUrl.trim() : '';
  const large2 = large.startsWith('blob:') ? '' : large;
  const prev2 = prev.startsWith('blob:') ? '' : prev;
  const url = large2 || prev2;
  if (url === '') return null;
  const kind: 'photo' | 'video' = m.kind === 'video' ? 'video' : 'photo';
  return { kind, url };
}

function blocked(
  platform: string,
  supported: boolean,
  reason: MirrorBlockedReason,
  caption: string,
): MirrorPost {
  return {
    platform,
    supported,
    format: 'blocked',
    caption,
    mediaUrl: null,
    mediaKind: null,
    link: '',
    linkPlacement: 'none',
    firstCommentNote: null,
    blockedReason: reason,
    relabelNeeded: false,
  };
}

/**
 * Instagram: photo -> image; video -> Reel; NONE -> blocked (ig_requires_media).
 * LINK-IN-BIO only — link_placement is ALWAYS 'none' and firstCommentNote ALWAYS
 * null; a link is never a caption/comment URL. (SocialPostModel::instagramPost.)
 */
function instagramPost(caption: string, media: MirrorResolvedMedia | null): MirrorPost {
  if (media === null) {
    return blocked('instagram', true, 'ig_requires_media', caption);
  }
  const format: MirrorFormat = media.kind === 'video' ? 'reel' : 'photo';
  return {
    platform: 'instagram',
    supported: true,
    format,
    caption,
    mediaUrl: media.url,
    mediaKind: media.kind,
    link: '',
    linkPlacement: 'none',
    firstCommentNote: null,
    blockedReason: null,
    relabelNeeded: stylingRelabelVisible(false, media),
  };
}

/**
 * Facebook: photo -> photo; video -> video; NONE -> text. The blog link goes to
 * a FIRST COMMENT by default; the per-post toggle may move it to caption-bottom
 * (never both). suggested_first_comment is NEVER merged into the caption.
 * (SocialPostModel::facebookPost.)
 */
function facebookPost(caption: string, media: MirrorResolvedMedia | null, draft: MirrorDraftInput): MirrorPost {
  let format: MirrorFormat;
  if (media === null) {
    format = 'text';
  } else if (media.kind === 'video') {
    format = 'video';
  } else {
    format = 'photo';
  }

  const link = typeof draft.link === 'string' ? draft.link.trim() : '';
  const suggested =
    typeof draft.suggestedFirstComment === 'string' && draft.suggestedFirstComment !== ''
      ? draft.suggestedFirstComment
      : null;

  const hasLinkContent = link !== '' || suggested !== null;
  let placement: MirrorLinkPlacement = 'none';
  if (hasLinkContent) {
    const requested = draft.linkPlacement ?? 'first_comment';
    placement = requested === 'first_comment' || requested === 'caption_bottom' || requested === 'none'
      ? requested
      : 'first_comment';
  }

  // Caption is body_text verbatim; ONLY caption-bottom appends the raw link.
  let finalCaption = caption;
  if (placement === 'caption_bottom' && link !== '') {
    finalCaption = caption === '' ? link : `${caption}\n\n${link}`;
  }

  // The first-comment string is only carried when that placement is chosen.
  const firstComment =
    placement === 'first_comment' ? (suggested ?? (link !== '' ? link : null)) : null;

  return {
    platform: 'facebook',
    supported: true,
    format,
    caption: finalCaption,
    mediaUrl: media ? media.url : null,
    mediaKind: media ? media.kind : null,
    link,
    linkPlacement: placement,
    firstCommentNote: firstComment,
    blockedReason: null,
    relabelNeeded: stylingRelabelVisible(false, media),
  };
}

/**
 * Map a Studio draft to the single per-platform post that will publish. Pure +
 * deterministic given `draft`. The FE mirror of SocialPostModel::fromDraft()
 * (which returns a one-element PlatformPost[] today — a single-channel draft, so
 * this returns the single MirrorPost directly).
 */
export function mirrorFromDraft(draft: MirrorDraftInput): MirrorPost {
  const caption = typeof draft.bodyText === 'string' ? draft.bodyText : '';
  const media = resolveMirrorMedia(draft.media);
  const channel = String(draft.channel ?? '').toLowerCase().trim();

  if (channel === 'instagram') return instagramPost(caption, media);
  if (channel === 'facebook') return facebookPost(caption, media, draft);

  // Unsupported channel -> honest blocked post (no publish target wired).
  return blocked(String(draft.channel ?? 'unknown'), false, 'unsupported_platform', caption);
}

/**
 * SINGLE SOURCE for "must this Studio frame carry the styling-preview relabel?".
 * True for EVERY baked/composed layout (even with no media — the composed
 * graphic never ships) and for ANY frame with a shipping media asset (which
 * ships RAW, without the framed styling). ContentPreview gates the on-frame
 * label with this, and the wired posts' `relabelNeeded` derives from it — so
 * the on-frame label and the mirror can never drift apart.
 */
export function stylingRelabelVisible(bakedLayout: boolean, media: MirrorResolvedMedia | null): boolean {
  return bakedLayout || media !== null;
}

/**
 * The relabel wording (text-based, non-dismissable; single source for tests +
 * render). With media: the raw image + caption is what posts. Without media:
 * the composed graphic does not post at all.
 */
export function stylingRelabelText(hasMedia: boolean): string {
  return hasMedia
    ? 'Styling preview — posts as image + caption'
    : 'Styling preview — this graphic does not post';
}

/** Human label for a mirror format (UI helper; text, not colour-coded). */
export function mirrorFormatLabel(format: MirrorFormat): string {
  switch (format) {
    case 'photo':
      return 'Photo post';
    case 'video':
      return 'Video post';
    case 'reel':
      return 'Reel';
    case 'text':
      return 'Text-only post';
    case 'blocked':
    default:
      return 'Will not publish';
  }
}
